// src/pages/quotations/QuotationDetail.jsx

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import "../../styles/print.css";
import {
  fetchQuotation, updateQuotation, createRevision,
  // eslint-disable-next-line no-unused-vars
  convertQuotationToProject, QUOTATION_STATUS, BOQ_SECTIONS,
} from "../../services/quotationService";
export default function QuotationDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const user     = JSON.parse(localStorage.getItem("user") || "{}");

  const [quotation, setQuotation] = useState(null);
  const [company,   setCompany]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [showConvert,  setShowConvert]  = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showRevision, setShowRevision] = useState(false);

  useEffect(() => { if (id) load(); }, [id]); // eslint-disable-line

  const load = async () => {
    try {
      setLoading(true);
      const [q, compSnap] = await Promise.all([
        fetchQuotation(id, user.company_id || ""),
        getDoc(doc(db, "companies", user.company_id || "")),
      ]);
      setQuotation(q);
      if (compSnap.exists()) setCompany(compSnap.data());
    } catch (err) {
      alert("Quotation not found");
      navigate("/quotations");
    } finally { setLoading(false); }
  };

  const handleStatusChange = async (status) => {
    try {
      setSaving(true);
      await updateQuotation(id, { status }, user.company_id || "");
      setQuotation(q => ({ ...q, status }));
    } catch (err) { alert("Failed: " + err.message); }
    finally { setSaving(false); }
  };

  const handleRevision = async () => {
    if (quotation.status === "draft") {
      alert("Cannot create revision of a Draft. Edit the quotation directly instead.");
      return;
    }
    if (!window.confirm("Create a new revision of this quotation?")) return;
    try {
      setSaving(true);
      const newId = await createRevision(id, user);
      alert("Revision created ✅");
      navigate(`/quotations/create?id=${newId}`);
    } catch (err) { alert("Failed: " + err.message); }
    finally { setSaving(false); }
  };

  const handleConvertToProject = () => {
    navigate("/projects/create", {
      state: {
        from_quotation: true,
        quotation_id:   id,
        quotation_no:   quotation.quotation_no,
        client_id:      quotation.client_id,
        client_name:    quotation.client_name,
        site_id:        quotation.site_id,
        site_name:      quotation.site_name,
        project_type:   quotation.project_type,
        total_value:    quotation.total_amount,
        description:    quotation.subject,
      }
    });
  };

  const fmtDate = (ts) => {
    if (!ts) return "—";
    if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric"
    });
    return ts;
  };

  if (loading)   return <div className="p-6 text-gray-500">Loading...</div>;
  if (!quotation) return <div className="p-6 text-gray-500">Not found.</div>;

  const statusInfo  = QUOTATION_STATUS[quotation.status] || QUOTATION_STATUS.draft;
  const itemsBySection = (section) =>
    (quotation.items || []).filter(i => i.section === section);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto pb-20 print-area">

      {/* PRINT HEADER — only shows on print */}
      <div className="print-only mb-6 border-b-2 border-gray-800 pb-4" style={{ display: "none" }}>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{company.company_name || "ZENO HVAC ERP"}</h1>
            <p className="text-sm text-gray-600 mt-0.5">{company.company_address || ""}</p>
            <p className="text-sm text-gray-600">
              {company.company_phone && `📞 ${company.company_phone}`}
              {company.company_email && `  ✉️ ${company.company_email}`}
            </p>
            {company.gst_number && (
              <p className="text-xs text-gray-500 mt-1">GSTIN: {company.gst_number}</p>
            )}
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-gray-800">QUOTATION</div>
            <div className="text-sm text-gray-600 mt-1">{quotation.quotation_no}</div>
            {quotation.revision > 0 && (
              <div className="text-xs text-amber-600">{quotation.revision_label}</div>
            )}
            <div className="text-xs text-gray-500 mt-1">
              Date: {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
            {quotation.validity_date && (
              <div className="text-xs text-gray-500">Valid Till: {quotation.validity_date}</div>
            )}
          </div>
        </div>
      </div>

      {/* HEADER */}
      <div className="flex justify-between items-start gap-3">
        <div>
          <button onClick={() => navigate("/quotations")}
            className="text-gray-400 text-sm hover:text-gray-600 mb-1 block">
            ← Quotations
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-800">{quotation.quotation_no}</h1>
            {quotation.revision > 0 && (
              <span className="text-sm bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
                {quotation.revision_label}
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">{quotation.subject}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => navigate(`/quotations/create?id=${id}`)}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-200">
            ✏️ Edit
          </button>
        </div>
      </div>

      {/* CLIENT INFO */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
        <h2 className="font-semibold text-gray-700 mb-3">Client Details</h2>
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          <div>
            <span className="text-gray-400 text-xs block">Client</span>
            <span className="font-medium">{quotation.client_name || "—"}</span>
          </div>
          <div>
            <span className="text-gray-400 text-xs block">Site</span>
            <span className="font-medium">{quotation.site_name || "—"}</span>
          </div>
          <div>
            <span className="text-gray-400 text-xs block">Project Type</span>
            <span className="font-medium capitalize">{quotation.project_type || "—"}</span>
          </div>
          <div>
            <span className="text-gray-400 text-xs block">Phone</span>
            <span className="font-medium">{quotation.client_phone || "—"}</span>
          </div>
          <div>
            <span className="text-gray-400 text-xs block">Email</span>
            <span className="font-medium">{quotation.client_email || "—"}</span>
          </div>
          <div>
            <span className="text-gray-400 text-xs block">Valid Till</span>
            <span className="font-medium">{quotation.validity_date || "—"}</span>
          </div>
          <div>
            <span className="text-gray-400 text-xs block">Payment Terms</span>
            <span className="font-medium">{quotation.payment_terms || "—"}</span>
          </div>
          <div>
            <span className="text-gray-400 text-xs block">Created</span>
            <span className="font-medium">{fmtDate(quotation.created_at)}</span>
          </div>
          <div>
            <span className="text-gray-400 text-xs block">Created By</span>
            <span className="font-medium">{quotation.created_by_name || "—"}</span>
          </div>
        </div>
      </div>

      {/* BOQ SECTIONS */}
      {Object.entries(BOQ_SECTIONS).map(([sectionKey, sectionInfo]) => {
        const sectionItems = itemsBySection(sectionKey);
        if (!sectionItems.length) return null;
        const sectionTotal = sectionItems.reduce((s, i) => s + Number(i.amount || 0), 0);

        return (
          <div key={sectionKey} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b">
              <div className="flex items-center gap-2">
                <span>{sectionInfo.icon}</span>
                <span className="font-semibold text-gray-700">{sectionInfo.label}</span>
              </div>
              <span className="font-bold text-gray-700">
                ₹{sectionTotal.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left w-8">#</th>
                    <th className="p-2 text-left">Item</th>
                    <th className="p-2 text-left">Brand</th>
                    <th className="p-2 text-left">Model</th>
                    <th className="p-2 text-left w-20">HSN</th>
                    <th className="p-2 text-center">Unit</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-right">Rate ₹</th>
                    <th className="p-2 text-right">Amount ₹</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sectionItems.map((item, i) => (
                    <tr key={i} className={item.is_optional ? "bg-yellow-50" : ""}>
                      <td className="p-2 text-center text-xs text-gray-400">{i + 1}</td>
                      <td className="p-2">
                        <span className="font-medium">{item.item_name || "—"}</span>
                        {item.remarks && (
                          <p className="text-xs text-gray-400 mt-0.5">{item.remarks}</p>
                        )}
                        {item.is_optional && (
                          <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-semibold ml-1">
                            Optional
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-gray-500 text-xs">{item.brand || "—"}</td>
                      <td className="p-2 text-gray-500 text-xs">{item.model || "—"}</td>
                      <td className="p-2 text-gray-500 text-xs">{item.hsn_code || "—"}</td>
                      <td className="p-2 text-center text-xs">{item.unit}</td>
                      <td className="p-2 text-right">{item.qty}</td>
                      <td className="p-2 text-right">₹{Number(item.rate || 0).toLocaleString("en-IN")}</td>
                      <td className="p-2 text-right font-semibold">
                        ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* TOTALS */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
        <div className="max-w-xs ml-auto space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Subtotal</span>
            <span>₹{Number(quotation.subtotal || 0).toLocaleString("en-IN")}</span>
          </div>
          {quotation.discount > 0 && (
            <div className="flex justify-between text-red-600">
              <span>Discount</span>
              <span>- ₹{Number(quotation.discount).toLocaleString("en-IN")}</span>
            </div>
          )}
          <div className="flex justify-between text-gray-500">
            <span>Tax ({quotation.tax_percent}%)</span>
            <span>₹{Number(quotation.tax_amount || 0).toLocaleString("en-IN")}</span>
          </div>
          <div className="flex justify-between font-bold text-lg border-t pt-2">
            <span>Total</span>
            <span className="text-green-700">
              ₹{Number(quotation.total_amount || 0).toLocaleString("en-IN")}
            </span>
          </div>
        </div>
      </div>

      {/* TERMS */}
      {(quotation.terms_conditions || quotation.notes) && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 space-y-3 text-sm">
          {quotation.terms_conditions && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Terms & Conditions</p>
              <p className="text-gray-700 whitespace-pre-wrap">{quotation.terms_conditions}</p>
            </div>
          )}
          {quotation.notes && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Notes</p>
              <p className="text-gray-700">{quotation.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* ACTIONS */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 space-y-3 no-print">
        <h2 className="font-semibold text-gray-700">Actions</h2>

        {/* Status change */}
        <div>
          <p className="text-xs text-gray-400 mb-2">Change Status:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(QUOTATION_STATUS)
              .filter(([key]) => key !== quotation.status)
              .map(([key, val]) => (
                <button key={key}
                  onClick={() => handleStatusChange(key)}
                  disabled={saving}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${val.color}`}>
                  → {val.label}
                </button>
              ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2 border-t border-gray-100">
          {/* Revision — sirf sent/negotiation/rejected pe dikhao */}
          {!quotation.converted_to_project &&
           ["sent", "negotiation", "rejected"].includes(quotation.status) && (
            <button
              onClick={handleRevision}
              disabled={saving}
              className="px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600">
              📝 Create Revision
            </button>
          )}

          {/* Convert to Project */}
          {(quotation.status === "accepted" || quotation.status === "sent") &&
           !quotation.converted_to_project && (
            <button
              onClick={handleConvertToProject}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700">
              🚀 Convert to Project
            </button>
          )}

          {/* Already converted */}
          {quotation.converted_to_project && quotation.project_id && (
            <button
              onClick={() => navigate(`/projects/${quotation.project_id}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700">
              📁 View Project
            </button>
          )}

          {/* Print */}
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-gray-700 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 no-print">
            🖨️ Print / PDF
          </button>
        </div>
      </div>
    </div>
  );
}